import React, { useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import Grid from '@mui/material/Grid';

import SendIcon from '@mui/icons-material/Send';

import makeStyles from '@mui/styles/makeStyles';
import QuickLinksPanel from '@ts-core/components/QuickLinksPanel';
import ProfessorSubjectsPanel, { useProfessorSubjectsPanel } from 'modules/subjects/components/ProfessorSubjectsPanel';
import GroupsPanel, { useGroupsPanel } from 'modules/groups/components/GroupsPanel';
import DashboardHeader from 'modules/dashboard/components/DashboardHeader';

import Dialog, { useDialog } from '@ts-core/components/Dialog';
import SpeedDialMenu from '@ts-core/components/SpeedDialMenu';

import { useQuickLinks } from 'modules/dashboard/hooks';
import { useCourse, useCourseActions } from 'modules/courses/hooks';
import { useStudents } from 'modules/students/hooks';

import MessagesPage from 'modules/messages/pages/messages/MessagesPage';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: theme.spacing(7),
  },
}));

const DashboardPage = () => {
  const { courseId } = useParams();
  const history = useHistory();
  const intl = useIntl();
  const classes = useStyles();

  const { course, loading: loadingCourse } = useCourse({
    courseId,
  });
  const { onTogglePeriod, changingPeriod } = useCourseActions();
  const { students } = useStudents({ courseId });

  const navigateToSubject = useCallback(
    (subject) => {
      history.push(`/${courseId}/subjects/${subject._id}/exercises`);
    },
    [history, courseId]
  );

  const navigateToGroup = useCallback(
    (group) => {
      history.push(`/${courseId}/groups/${group._id}`);
    },
    [history, courseId]
  );

  const handleToggleCourseStatus = useCallback(() => {
    if (course) {
      onTogglePeriod(course);
    }
  }, [onTogglePeriod, course]);

  const quickLinks = useQuickLinks();

  const subjectsPanelProps = useProfessorSubjectsPanel({
    courseId,
    onSelectLeafSubject: navigateToSubject,
  });

  const groupsPanelProps = useGroupsPanel({
    courseId,
    onSelectGroup: navigateToGroup,
  });

  const { onOpenDialog: onOpenSendMessageDialog, onCloseDialog: onCloseSendMessageDialog, dialogProps: sendMessageDialogProps } = useDialog();

  const handleOpenSendMessagesDialog = useCallback(() => {
    onOpenSendMessageDialog({
      title: intl.formatMessage({ id: 'dialog.messages.title' }),
    });
  }, [onOpenSendMessageDialog, intl]);

  /**
   * Speed dial options
   */
  const speedDialOptions = useMemo(() => {
    return [
      {
        title: <FormattedMessage id="button.sendMessage.title" />,
        icon: <SendIcon />,
        onClick: handleOpenSendMessagesDialog,
      },
    ];
  }, [handleOpenSendMessagesDialog]);

  return (
    <>
      <Grid container spacing={2} className={classes.root}>
        <Grid item xs={12}>
          <DashboardHeader course={course} loading={loadingCourse || changingPeriod} onToggleCourseStatus={handleToggleCourseStatus} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <QuickLinksPanel quickLinks={quickLinks} />
            </Grid>
            <Grid item xs={12}>
              <GroupsPanel {...groupsPanelProps} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ProfessorSubjectsPanel {...subjectsPanelProps} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <SpeedDialMenu options={speedDialOptions} />
      <Dialog {...sendMessageDialogProps}>
        <MessagesPage closeEnclosingDialog={onCloseSendMessageDialog} courseId={courseId} students={students} />
      </Dialog>
    </>
  );
};

export default DashboardPage;
